import { useEffect, useRef } from 'react'
import { services } from '../../data/constants'
import styles from './Services.module.css'

const Services = () => {
  const servicesListRef = useRef<HTMLDivElement>(null)
  const serviceItemsRef = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const observerOptions = {
      threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1],
      rootMargin: '0px'
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains(styles.animated)) {
          entry.target.classList.add(styles.animated)
          
          const cards = entry.target.querySelectorAll(`[data-animate]`)
          if (cards.length > 0) {
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add(styles.animate)
              }, index * 250)
            })
          }
        }
      })
    }, observerOptions)

    if (servicesListRef.current) {
      observer.observe(servicesListRef.current)
    }

    // Scroll-based stacking animation with 3D effect (no mouse interaction)
    let ticking = false
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (!servicesListRef.current) return

          const windowHeight = window.innerHeight

          serviceItemsRef.current.forEach((item, index) => {
            if (!item) return

            const rect = item.getBoundingClientRect()
            const itemCenter = rect.top + rect.height / 2
            const viewportCenter = windowHeight / 2
            const distanceFromCenter = itemCenter - viewportCenter
            const scrollProgress = 1 - Math.abs(distanceFromCenter) / (windowHeight + rect.height)

            // 3D rotation based on scroll position (subtle 3D effect)
            const rotateX = Math.max(-5, Math.min(5, (distanceFromCenter / windowHeight) * 5))
            const rotateY = 0 // No Y rotation for cleaner look

            if (scrollProgress > 0 && distanceFromCenter < windowHeight) {
              // Stacking effect: cards stack on top of each other as they scroll
              const translateY = Math.max(-50, distanceFromCenter * 0.3 - index * 30)
              const scale = 0.92 + Math.min(0.08, scrollProgress * 0.08)
              const opacity = Math.min(1, 0.3 + scrollProgress * 0.7)
              const zIndex = services.length - index + Math.floor(scrollProgress * 5)

              item.style.transform = `perspective(1000px) translateY(${translateY}px) scale(${scale}) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
              item.style.opacity = opacity.toString()
              item.style.zIndex = zIndex.toString()
            } else if (distanceFromCenter >= windowHeight) {
              // Card is below viewport
              item.style.transform = `perspective(1000px) translateY(0) scale(1) rotateX(0deg) rotateY(0deg)`
              item.style.opacity = '1'
              item.style.zIndex = (services.length - index).toString()
            } else {
              // Card is above viewport
              item.style.transform = `perspective(1000px) translateY(0) scale(0.95) rotateX(0deg) rotateY(0deg)`
              item.style.opacity = '0.5'
              item.style.zIndex = (services.length - index).toString()
            }
          })

          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Initial call

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <section id="services" className={styles.services}>
      <div className={styles.container}>
        <div className={styles.servicesHeader}>
          <p className={styles.pill}>Eğitim Anlayışımız</p>
          <h2 className={styles.sectionTitle}>Eğitim Anlayışımız</h2>
          <p className={styles.sectionSubtitle}>
            Öğrencilerimize pratik deneyim ve gerçek projelerle eğitim sunuyoruz
          </p>
        </div>
        <div className={styles.servicesList} ref={servicesListRef} data-animate>
          {services.map((service, index) => (
            <article
              key={service.id}
              ref={(el) => {
                serviceItemsRef.current[index] = el as HTMLDivElement
              }}
              className={`${styles.serviceItem} ${styles[`service${index + 1}`]}`}
              data-animate
              style={{
                position: 'relative',
                zIndex: services.length - index
              }}
            >
              <div className={styles.serviceText}>
                <span className={styles.serviceTag}>{service.tag}</span>
                <div className={styles.serviceNumberLarge}>{service.number}</div>
                <h3 className={styles.serviceTitle}>
                  {service.title.split('\n').map((line, i) => (
                    <span key={i}>
                      {line}
                      {i < service.title.split('\n').length - 1 && <br />}
                    </span>
                  ))}
                </h3>
                <div className={styles.serviceDivider}></div>
                <p className={styles.serviceDescription}>{service.description}</p>
              </div>
              <div className={styles.serviceImage}>
                <img src={service.image} alt={service.title} />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Services
